// React and core libraries
import React, { Component } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./styles/form.css";
import "./styles/buttons.css";
import "./styles/container.css";
import "./styles/blox-brand.css";
import {
  withRouter,
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";

// Internal utilities
import { utils } from "./utils/Utils.jsx";
import { createVersionUpdateManager } from "./utils/versionUpdateManager.js";
import {
  installGlobalErrorHandlers,
  setReporterAppVersion,
  setReporterAuthToken,
} from "./utils/errorReporter";
import ScrollToTop from "./utils/scrollToTop.js";
import RemoveGoBackAutoScroll from "./utils/removeGoBackAutoScroll.js";
import { getFlagCountryCode } from "./locales/commonDictionary.js";
import ErrorBoundary from "./components/errorBoundary/ErrorBoundary.jsx";
import { ToastProvider } from "./components/toast/Toast.jsx";
import NetworkStatus from "./components/network-status/NetworkStatus.jsx";

import Modal from "react-bootstrap/Modal";
import { Spinner } from "react-bootstrap";
import { IoIosArrowForward } from "react-icons/io";
import { AiOutlineClose } from "react-icons/ai";
import Flag from "react-world-flags";

import ResultPage from "./pages/result/ResultPage.jsx";
import Dashboard from "./pages/dashboard/Dashboard.jsx";

const Login = React.lazy(() => import("./pages/login/Login.jsx"));
const BloxTerms = React.lazy(
  () => import("./pages/terms/blox-terms/BloxTerms.jsx"),
);
const ChangePassword = React.lazy(
  () => import("./pages/change-password/ChangePassword.jsx"),
);
const CorporateRegister = React.lazy(
  () => import("./pages/register/corporate-register/CorporateRegister.jsx"),
);
const ForgotPassword = React.lazy(
  () => import("./pages/forgot-password/ForgotPassword.jsx"),
);
const NewVersionAvailableModal = React.lazy(
  () =>
    import("./components/modals/new-version-available/NewVersionAvailableModal.jsx"),
);
const PassCode = React.lazy(() => import("./pages/pass-code/PassCode.jsx"));
const Register = React.lazy(
  () => import("./pages/register/register2/Register.jsx"),
);
const TwoSTepVerification = React.lazy(
  () => import("./pages/twostep-verification/TwoStepVerification.jsx"),
);

class App extends Component {
  state = {
    region: "en",
    stylePath: "/themes/dark/darkTheme.css",
    theme: "dark",
    localizedText: require("./locales/textEn.json"),
    visibility: "hidden",
    showModal: false,

    inactivityTime: process.env.REACT_APP_ENV === "prod" ? 10 : 1440,

    config: {},
    token: "",

    adminRoles: ["Company_Admin", "Blox_Admin"],

    errorInfo: {},

    version: "v.2.0.0",
    updateAvailable: false,
    buildVersion: "",
    nextBuildVersion: "",

    listLanguages: [
      {
        alpha3_b: "eng",
        alpha2: "en",
        properName: "English",
        code: "EN",
      },
      {
        alpha3_b: "spa",
        alpha2: "es",
        properName: "Español",
        code: "ES",
      },
      {
        alpha3_b: "por",
        alpha2: "pt",
        properName: "Portugues do Brasil",
        code: "PT",
      },
      {
        alpha3_b: "tur",
        alpha2: "tr",
        properName: "Türkçe",
        code: "TR",
      },
      {
        alpha3_b: "kor",
        alpha2: "kr",
        properName: "한국어",
        code: "KR",
      },
      {
        alpha3_b: "jpn",
        alpha2: "ja",
        properName: "日本語",
        code: "JA",
      },
    ],
  };

  constructor(props) {
    super(props);

    //invalidation console function for higher enviorments
    if (process.env.REACT_APP_ENV == "prod") console.log = () => {};

    this.timeoutRef = null;
    this.versionUpdateManager = createVersionUpdateManager({
      getUpdateAvailable: () => this.state.updateAvailable,
      onInitialVersion: (version) => {
        this.setState({
          buildVersion: version,
        });
      },
      onUpdateAvailable: (version) => {
        this.setState({
          updateAvailable: true,
          nextBuildVersion: version,
        });
      },
    });
    this.handleInactivity = this.handleInactivity.bind(this);
    this.resetTimer = this.resetTimer.bind(this);

    window.onerror = (message, source, lineno, colno, error) => {
      setTimeout(() => {
        if (!window.errorHandledByBoundary) {
          console.log("Error caught by Global Error Catcher:", error);
          this.setState(
            {
              errorInfo: error,
            },
            () => {
              console.log("STATE ERROR", this.state.errorInfo);
            },
          );
        } else {
          console.log("Error was already handled by ErrorBoundary.");
          window.errorHandledByBoundary = false; // Reset the flag
        }
      }, 1000); // Delay execution to the next event loop cycle
    };
  }

  componentDidMount() {
    /* Install global error handlers and pull build id from version.json
       so error reports are correlated to a deploy. Best-effort fetch;
       silent failure leaves app_version blank rather than blocking. */
    installGlobalErrorHandlers();
    fetch("/version.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => { if (v?.version) setReporterAppVersion(v.version); })
      .catch(() => {});

    const events = ["mousemove", "keydown", "scroll", "click"];
    events.forEach((event) => {
      window.addEventListener(event, this.resetTimer);
    });

    this.checkForSavedRegion();
    this.checkForSavedTheme();

    // Check if the page has already loaded
    if (document.readyState === "complete") {
      this.setState({
        visibility: "visible",
      });
    } else {
      window.addEventListener("load", this.onPageLoad);
    }
    window.addEventListener("beforeunload", this.savePathNameBeforeRefresh);

    /* Open the language picker modal in response to a global event,
       so any header-chrome button (LanguageButton) can request it
       without prop-drilling handleModalOpen through dozens of routes. */
    window.addEventListener("blox:open-language-modal", this.handleModalOpen);

    // Set the initial timer
    this.resetTimer();

    this.versionUpdateManager.start();

    //test
    console.log("scale", Math.round(window.devicePixelRatio * 100));
    console.log("windowWith", window.innerWidth);

    console.log("utils.getXAPIKey", utils.getXAPIKey());
  }

  componentWillUnmount() {
    window.removeEventListener("load", this.onPageLoad);
    window.removeEventListener("beforeunload", this.savePathNameBeforeRefresh); // remove the event handler for normal unmounting
    window.removeEventListener("blox:open-language-modal", this.handleModalOpen);

    const events = ["mousemove", "keydown", "scroll", "click"];

    events.forEach((event) => {
      window.removeEventListener(event, this.resetTimer);
    });

    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }

    this.versionUpdateManager.stop();
  }

  handleRefreshApp = () => {
    const next = this.state.nextBuildVersion || this.state.buildVersion;
    if (next) {
      localStorage.setItem("appBuildVersion", next);
    }
    window.location.reload();
  };

  savePathNameBeforeRefresh = () => {
    if (
      window.location.pathname != "/" &&
      window.location.pathname.includes("/dashboard") &&
      !window.errorHandledByBoundary &&
      Object.keys(this.state.errorInfo).length == 0 //no error
    ) {
      console.log("savePathNameBeforeRefresh");
      sessionStorage.setItem("savedPathname", window.location.pathname);
    } else {
      console.log("this.state.errorInfo", this.state.errorInfo);
      console.log(
        "window.errorHandledByBoundary",
        window.errorHandledByBoundary,
      );
    }
  };

  onPageLoad = () => {
    this.setState({
      visibility: "visible",
    });
  };

  checkForSavedTheme() {
    // Force dark theme — the redesign is built for dark mode
    localStorage.setItem("theme", "dark");
    this.setState({
      stylePath: "/themes/dark/darkTheme.css",
      theme: "dark",
    });
  }

  checkForSavedRegion() {
    if (localStorage.getItem("region")) {
      console.log("entering here");
      this.setState(
        {
          region: localStorage.getItem("region"),
        },
        () => {
          console.log("this.state.region", this.state.region);
          this.changeRequiredText(this.state.region);
        },
      );
    } else {
      this.setState(
        {
          region: "en",
        },
        () => {
          this.changeRequiredText("en");
        },
      );
    }
  }

  changeTheme = (theme) => {
    if (theme == "dark") {
      this.setState({
        stylePath: "/themes/dark/darkTheme.css",
      });
    } else {
      this.setState({
        stylePath: "/themes/light/lightTheme.css",
      });
    }
    localStorage.setItem("theme", theme);
    this.setState({
      theme: theme,
    });
  };

  changeRegion = (region, source) => {
    console.log("Changing region to:", region);
    if (source) {
      console.log("Source of changeRegion:", source);
    }
    this.setState(
      {
        region: region,
      },
      () => {
        localStorage.setItem("region", region);
        this.changeRequiredText(region);
        this.setState({
          showModal: false,
        });
      },
    );
  };

  changeRequiredText = (region) => {
    if (region == "en") {
      this.setState({
        localizedText: require("./locales/textEn.json"),
      });
    } else if (region == "es") {
      this.setState({
        localizedText: require("./locales/textEs.json"),
      });
    } else if (region == "pt") {
      this.setState({
        localizedText: require("./locales/textPt.json"),
      });
    } else if (region == "kr") {
      this.setState({
        localizedText: require("./locales/textKo.json"),
      });
    } else if (region == "tr") {
      this.setState({
        localizedText: require("./locales/textTr.json"),
      });
    } else if (region == "ja") {
      this.setState({
        localizedText: require("./locales/textJa.json"),
      });
    } else {
      this.setState({
        localizedText: require("./locales/textEn.json"),
      });
    }
  };

  handleModalOpen = () => {
    this.setState({
      showModal: true,
    });
  };

  handleModalClose = () => {
    this.setState({
      showModal: false,
    });
  };

  setConfig = (config) => {
    console.log("config being set", config);
    this.setState({
      config: config,
    });
  };

  setToken = (token) => {
    /* Keep the error reporter's auth context in sync so reports go
       up correlated to the active session (or anonymous when token
       is cleared on logout). */
    setReporterAuthToken(token);
    this.setState(
      {
        token: token,
      },
      () => {
        console.log("this.state.token", this.state.token);
        if (this.state.token != "") {
          console.log("goes here 0");
          if (
            sessionStorage.getItem("savedPathname") != null &&
            sessionStorage.getItem("savedPathname") != "" &&
            sessionStorage.getItem("savedPathname").includes("/dashboard")
          ) {
            console.log(
              "going to saved pathname:",
              sessionStorage.getItem("savedPathname"),
            );
            utils.goToURL(this.props, sessionStorage.getItem("savedPathname"));
            sessionStorage.removeItem("savedPathname");
          } else {
            console.log("goes here 2");
            utils.goToURL(this.props, "/dashboard");
          }
        } else {
          console.log("goes here 3");
          utils.goToURL(this.props, "/");
        }
      },
    );
  };

  handleInactivity() {
    if (
      Object.keys(this.state.config).length >= 0 &&
      this.state.token != "" &&
      window.location.pathname != "/verification" &&
      window.location.pathname != "/pass-code"
    ) {
      this.setState(
        {
          token: "",
        },
        () => {
          this.props.history.push({
            pathname: "/pass-code",
            megastate: {
              type: "lockedEnterPassCode",
            },
          });
        },
      );
    }
  }

  resetTimer() {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
    if (this.state.inactivityTime != 0) {
      this.timeoutRef = setTimeout(
        this.handleInactivity,
        this.state.inactivityTime * 60 * 1000,
      ); // x minutes
    }
  }

  render() {
    return (
      <React.Suspense
        fallback={
          <div
            className="pulse-animation"
            style={{
              height: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="loading-config-div fade-in-up">
              <Spinner size="m"></Spinner>
              <div>{this.state.localizedText["loading"]}...</div>
            </div>
          </div>
        }
      >
        <ToastProvider>
        <React.Fragment>
          <NetworkStatus localizedText={this.state.localizedText} />
          <div
            style={{
              "font-family": process.env.REACT_APP_FONT,
              visibility: this.state.visibility,
            }}
          >
            <link
              rel="stylesheet"
              type="text/css"
              href={this.state.stylePath}
            />
            <ScrollToTop />
            <RemoveGoBackAutoScroll />
            <ErrorBoundary
              localizedText={this.state.localizedText}
              errorInfo={this.state.errorInfo}
              caughtAt="App"
            >
              <Switch>
                <Route
                  path="/dashboard"
                  render={(props) =>
                    this.state.token != "" &&
                    Object.keys(this.state.config).length > 0 ? (
                      <Dashboard
                        changeTheme={this.changeTheme}
                        theme={this.state.theme}
                        handleModalOpen={this.handleModalOpen}
                        region={this.state.region}
                        localizedText={this.state.localizedText}
                        config={this.state.config}
                        token={this.state.token}
                        setConfig={this.setConfig}
                        setToken={this.setToken}
                        handleInactivity={this.handleInactivity}
                        adminRoles={this.state.adminRoles}
                        version={this.state.version}
                        changeRegion={this.changeRegion}
                      ></Dashboard>
                    ) : (
                      <Redirect to="/pass-code" />
                    )
                  }
                />
                <Route
                  exact
                  path="/verification"
                  render={(props) => (
                    <TwoSTepVerification
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                      setConfig={this.setConfig}
                      setToken={this.setToken}
                      version={this.state.version}
                    ></TwoSTepVerification>
                  )}
                />
                <Route
                  exact
                  path="/register"
                  render={(props) =>
                    process.env.REACT_APP_ALLOWED_USERS ? (
                      <Redirect to="/" />
                    ) : (
                      <Register
                        changeTheme={this.changeTheme}
                        theme={this.state.theme}
                        handleModalOpen={this.handleModalOpen}
                        region={this.state.region}
                        localizedText={this.state.localizedText}
                        listLanguages={this.state.listLanguages}
                      ></Register>
                    )
                  }
                />{" "}
                <Route
                  exact
                  path="/corporate-register"
                  render={(props) => (
                    <CorporateRegister
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                    ></CorporateRegister>
                  )}
                />
                <Route
                  exact
                  path="/result"
                  render={(props) => (
                    <ResultPage
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                      resultType="outside"
                    ></ResultPage>
                  )}
                />
                <Route
                  exact
                  path="/forgot-password"
                  render={(props) => (
                    <ForgotPassword
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                      version={this.state.version}
                    ></ForgotPassword>
                  )}
                />
                <Route
                  exact
                  path="/change-password"
                  render={(props) => (
                    <ChangePassword
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                      token={this.state.token}
                      setConfig={this.setConfig}
                      setToken={this.setToken}
                    ></ChangePassword>
                  )}
                />
                <Route
                  exact
                  path="/pass-code"
                  render={(props) => (
                    <PassCode
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                      token={this.state.token}
                      config={this.state.config}
                      setConfig={this.setConfig}
                      setToken={this.setToken}
                    ></PassCode>
                  )}
                />
                <Route
                  exact
                  path="/"
                  render={(props) => (
                    <Login
                      changeTheme={this.changeTheme}
                      theme={this.state.theme}
                      handleModalOpen={this.handleModalOpen}
                      region={this.state.region}
                      localizedText={this.state.localizedText}
                      setToken={this.setToken}
                    ></Login>
                  )}
                />
                <Route
                  exact
                  path="/terms/:region"
                  render={(props) => <BloxTerms></BloxTerms>}
                />
                <Route path="/" render={(props) => <Redirect to="/" />} />
              </Switch>
            </ErrorBoundary>
          </div>
          <Modal
            show={this.state.showModal}
            onHide={this.handleModalClose}
            className="primary-modal theme-region-modal"
            centered
          >
            <Modal.Header>
              <Modal.Title>
                {this.state.localizedText["select-language"]}
              </Modal.Title>
              <AiOutlineClose
                className="close-button"
                onClick={this.handleModalClose}
              />
            </Modal.Header>
            <Modal.Body>
              <div class="region-list">
                {this.state.listLanguages.map((language) => (
                  <div
                    key={language.alpha2}
                    class="region-row"
                    onClick={this.changeRegion.bind(this, language.alpha2)}
                  >
                    <div class="text-column">
                      <Flag
                        className="flag-icon theme-toggle-icon"
                        code={getFlagCountryCode(
                          language.code,
                          this.state.config.domicile
                            ? this.state.config.domicile
                            : language.code,
                        )}
                      />
                      {language.properName} ({language.alpha2.toUpperCase()})
                    </div>

                    <IoIosArrowForward class="arrow"></IoIosArrowForward>
                  </div>
                ))}
              </div>
            </Modal.Body>
          </Modal>
          <NewVersionAvailableModal
            show={this.state.updateAvailable}
            onHide={this.handleRefreshApp}
            localizedText={this.state.localizedText}
          />
        </React.Fragment>
        </ToastProvider>
      </React.Suspense>
    );
  }
}

export default withRouter(App);
